import React, { useRef, useEffect, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../../styles/components/Dropdown.css';

interface DropdownItem {
  label: string;
  href?: string;
  to?: string;
  icon?: string;
  onClick?: () => void;
}

interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  containerClassName?: string;
  dropdownClassName?: string;
  triggerClassName?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  items,
  isOpen,
  onToggle,
  onClose,
  containerClassName = '',
  dropdownClassName = 'follow-dropdown',
  triggerClassName = ''
}) => {
  const containerRef = useRef<HTMLLIElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <motion.li 
      ref={containerRef}
      className={`follow-dropdown-container ${containerClassName}`}
    >
      <div 
        onClick={onToggle}
        className={triggerClassName}
      >
        {trigger}
      </div>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            className={dropdownClassName}
            style={{
              background: 'rgba(0, 0, 0, 0.25)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
            initial={{ 
              opacity: 0, 
              y: 10, 
              scale: 0.95
            }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1
            }}
            exit={{ 
              opacity: 0, 
              y: 10, 
              scale: 0.95
            }}

            transition={{ 
              duration: 0.2,
              ease: [0.4, 0, 0.2, 1]
            }}
          >
            <ul className="dropdown-links">
              {items.map((item, index) => (
                <li key={index}>
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dropdown-link"
                      onClick={() => onClose()}
                    >
                      {item.icon && <span style={{ marginRight: '8px' }}>{item.icon}</span>}
                      {item.label}
                    </a>
                  ) : (
                    <button
                      onClick={() => {
                        item.onClick?.();
                        onClose();
                      }}
                      className="dropdown-link"
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        width: '100%', 
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: 'inherit'
                      }}
                    >
                      {item.icon && <span style={{ marginRight: '8px' }}>{item.icon}</span>}
                      {item.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
};

export default Dropdown; 